/**
 * Music scheduling rules (DOM-free). Gaps follow vanilla MusicManager-style
 * windows: overworld 5-20 min between tracks, creative 1-3 min, nether 1.5-4
 * min, menu immediate. Mood switches shorten a pending wait.
 */
import type { MusicMood } from './registry';

/** [min, max] seconds of silence before the next track. */
export const GAP: Record<MusicMood, [number, number]> = {
  menu: [0, 2],
  game: [300, 1200],
  creative: [60, 180],
  nether: [90, 240],
};

/** Delay before the first track after a world loads. */
export const FIRST_DELAY: [number, number] = [5, 30];

export function gapFor(mood: MusicMood, rnd: () => number): number {
  const [a, b] = GAP[mood];
  return a + (b - a) * rnd();
}

/**
 * Remaining wait after switching to `mood`: never longer than what that mood
 * would pick fresh, so walking into the nether or opening the title screen
 * does not inherit a 20-minute overworld gap.
 */
export function waitAfterMoodChange(remaining: number, mood: MusicMood, rnd: () => number): number {
  return Math.min(remaining, gapFor(mood, rnd));
}

/** Procedural piece length in seconds (C418 tracks run ~2-5 min; placeholders are shorter). */
export function pieceLength(mood: MusicMood, rnd: () => number): number {
  return mood === 'menu' ? 150 + rnd() * 90 : 100 + rnd() * 110;
}

/** Scales (semitone offsets) per mood: pentatonic/lydian calm, phrygian dark for the nether. */
export const SCALES: Record<MusicMood, number[][]> = {
  menu: [[0, 2, 4, 6, 7, 9, 11], [0, 2, 4, 7, 9]],
  game: [[0, 2, 4, 7, 9], [0, 2, 3, 5, 7, 9, 10], [0, 2, 4, 6, 7, 9, 11]],
  creative: [[0, 2, 4, 7, 9], [0, 2, 4, 6, 7, 9, 11]],
  nether: [[0, 1, 3, 5, 7, 8, 10], [0, 1, 4, 5, 7, 8, 11]],
};

/** MIDI note -> Hz. */
export const midiHz = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);

/** Scale degree (may be negative / beyond one octave) -> MIDI note. */
export function degreeToMidi(root: number, scale: number[], deg: number): number {
  const n = scale.length;
  const oct = Math.floor(deg / n);
  return root + oct * 12 + scale[((deg % n) + n) % n];
}
